import React from 'react'
import { useTranslation } from 'react-i18next'

import { BulletLabel } from 'components'
import { sortProductCategories } from 'features/StockKeepingUnit'
import {
  setValue,
  setPriceFormatter,
  setMaintenancePeriodFormat,
} from 'helpers/common'
import { getBoxTitle } from 'helpers/language'

import SkeletonStockKeepingDetail from './SkeletonStockKeepingDetail'

import type { IStockKeepingUnitDetail } from 'features/StockKeepingUnit'
import { StockKeepingUnitPreviewImage } from 'features/StockKeepingUnit'

interface Props {
  data: IStockKeepingUnitDetail
  loading: boolean
}

interface BoxItemProps {
  title: string | JSX.Element
  description?: string | boolean | number | JSX.Element | null
}

const base = 'f-stock-keeping-unit-detail'

export const BoxItem = ({ title, description }: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

const StockKeepingUnitDetail: React.FC<Props> = ({ data, loading }: Props) => {
  const { t } = useTranslation(['sku', 'common'])

  const {
    photos,
    thumbnail,
    name,
    isActive: _isActive,
    code,
    brand,
    detail,
    categories,
    costPrice,
    costCurrency,
    salePrice,
    saleCurrency,
    maintenancePeriod,
    maintenanceUnit,
  } = data

  const Description = () => {
    const _categories = sortProductCategories(categories)
    const _costPrice = setPriceFormatter(costPrice, costCurrency)
    const _salePrice = setPriceFormatter(salePrice, saleCurrency)
    const _maintenancePeriod = setMaintenancePeriodFormat(
      maintenancePeriod,
      maintenanceUnit,
    )

    const isActive = (
      <BulletLabel
        isActive={_isActive}
        isShowLabel={true}
        className={`${base}__status-description`}
      />
    )

    return (
      <>
        <div className={`${base}__image-layout`}>
          <span className={`${base}__product-detail-title`}>
            {t('sku:product.detail.label')}
          </span>
          <StockKeepingUnitPreviewImage photos={photos} thumbnail={thumbnail} />
        </div>
        <div className={`${base}__description-layout`}>
          <div className={`${base}__description-layout-status`}>
            <BoxItem title='sku:sku.title' description={code} />
            <BoxItem title='sku:status.title' description={isActive} />
          </div>
          <BoxItem title='sku:productName.title' description={name} />
          <BoxItem title='sku:brand.title' description={brand} />
          <BoxItem title='sku:category.title' description={_categories} />
          <div className={`${base}__description-layout-status`}>
            <BoxItem title='sku:costPrice.title' description={_costPrice} />
            <BoxItem title='sku:salePrice.title' description={_salePrice} />
          </div>
          <BoxItem
            title='sku:maintenancePeriod.title'
            description={_maintenancePeriod}
          />
          <BoxItem title='common:address.remark.label' description={detail} />
        </div>
      </>
    )
  }

  return (
    <div className={base}>
      {loading ? <SkeletonStockKeepingDetail /> : <Description />}
    </div>
  )
}

export default StockKeepingUnitDetail
